<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import wootConstants from 'dashboard/constants/globals';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const store = useStore();
const { t } = useI18n();

const currentChat = useMapGetter('getSelectedChat');
const teams = useMapGetter('teams/getTeams');

const dialogRef = ref(null);
const selectedTeamId = ref('');
const isLoading = ref(false);

const teamOptions = computed(() => {
  const currentTeamId = currentChat.value?.meta?.team?.id;
  return teams.value
    .filter(team => team.id !== currentTeamId)
    .map(team => ({ value: team.id, label: team.name }));
});

const open = () => {
  selectedTeamId.value = '';
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const handleConfirm = async () => {
  const conversationId = currentChat.value.id;
  const selectedTeam = teams.value.find(
    team => team.id === selectedTeamId.value
  );
  if (!selectedTeam) {
    return;
  }

  isLoading.value = true;
  try {
    if (currentChat.value.status !== wootConstants.STATUS_TYPE.OPEN) {
      await store.dispatch('toggleStatus', {
        conversationId,
        status: wootConstants.STATUS_TYPE.OPEN,
      });
    }

    store.dispatch('setCurrentChatAssignee', {
      conversationId,
      assignee: null,
    });
    await store.dispatch('assignAgent', {
      conversationId,
      agentId: null,
    });

    store.dispatch('setCurrentChatTeam', {
      conversationId,
      team: selectedTeam,
    });
    await store.dispatch('assignTeam', {
      conversationId,
      teamId: selectedTeam.id,
    });

    await store.dispatch('createPendingMessageAndSend', {
      conversationId,
      message: t('CONVERSATION.TRANSFER_TEAM_MESSAGE', {
        teamName: selectedTeam.name,
      }),
      private: false,
    });

    useAlert(t('CONVERSATION.TRANSFER_TEAM_SUCCESS'));
    close();
  } catch (error) {
    useAlert(t('CONVERSATION.TRANSFER_TEAM_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="sm"
    :title="t('CONVERSATION.TRANSFER_TEAM_MODAL.TITLE')"
    :description="t('CONVERSATION.TRANSFER_TEAM_MODAL.DESCRIPTION')"
    :confirm-button-label="t('CONVERSATION.TRANSFER_TEAM_MODAL.CONFIRM')"
    :cancel-button-label="t('CONVERSATION.TRANSFER_TEAM_MODAL.CANCEL')"
    :disable-confirm-button="!selectedTeamId"
    :is-loading="isLoading"
    @confirm="handleConfirm"
  >
    <div class="flex flex-col gap-2">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CONVERSATION.TRANSFER_TEAM_MODAL.TEAM_LABEL') }}
      </label>
      <ComboBox
        v-model="selectedTeamId"
        :options="teamOptions"
        :placeholder="t('CONVERSATION.TRANSFER_TEAM_MODAL.TEAM_LABEL')"
        class="w-full"
      />
    </div>
  </Dialog>
</template>
